import React, { useEffect, useMemo, useState } from "react";
import "./ClassAnalysis.css";
import axios from "../axios";
import { LineChart, Line, YAxis, CartesianGrid, Tooltip } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons";

function ClassAnalysis({
  student,
  startDate,
  endDate,
  assessmentTypeId,
  subjectWiseData,
  subjectList,
}) {
  const [selectedSupplement, setSelectedSupplement] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [studenttopperData, setStudenttopperData] = useState([]);
  const [studentYourData, setStudentYourData] = useState([]);
  const [studentAverageData, setStudentAverageData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (
      !startDate ||
      !endDate ||
      !student?.id ||
      !student?.standard?.id ||
      !student?.section?.id
    )
      return;
    const fetchstudenttopperData = async () => {
      try {
        const response = await axios.get(
          `/performance/standard/${student?.standard?.id}/section/${student?.section?.id}/student/${student?.id}/topperOverallClassPerformance/get?startDate=${startDate}&endDate=${endDate}&subjectId=${selectedSubject}&assessmentEventId=${assessmentTypeId}`,
        );
        setStudenttopperData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchstudenttopperData();
  }, [startDate, endDate, student, selectedSubject, assessmentTypeId]);

  useEffect(() => {
    if (
      !startDate ||
      !endDate ||
      !student?.id ||
      !student?.standard?.id ||
      !student?.section?.id
    )
      return;
    const fetchstudentYourData = async () => {
      try {
        const response = await axios.get(
          `/performance/standard/${student?.standard?.id}/section/${student?.section?.id}/student/${student?.id}/yourOverallClassPerformance/get?startDate=${startDate}&endDate=${endDate}&subjectId=${selectedSubject}&assessmentEventId=${assessmentTypeId}`,
        );
        setStudentYourData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchstudentYourData();
  }, [startDate, endDate, student, selectedSubject, assessmentTypeId]);

  useEffect(() => {
    if (
      !startDate ||
      !endDate ||
      !student?.id ||
      !student?.standard?.id ||
      !student?.section?.id
    )
      return;
    const fetchstudentAverageData = async () => {
      try {
        const response = await axios.get(
          `/performance/standard/${student?.standard?.id}/section/${student?.section?.id}/student/${student?.id}/averageOverallClassPerformance/get?startDate=${startDate}&endDate=${endDate}&subjectId=${selectedSubject}&assessmentEventId=${assessmentTypeId}`,
        );
        setStudentAverageData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchstudentAverageData();
  }, [startDate, endDate, student, selectedSubject, assessmentTypeId]);

  useEffect(() => {
    if (
      !startDate ||
      !endDate ||
      !student?.id ||
      !student?.standard?.id ||
      !student?.section?.id
    )
      return;
    const fetchstudentYourData = async () => {
      try {
        const response = await axios.get(
          `/performance/standard/${student?.standard?.id}/section/${student?.section?.id}/student/${student?.id}/yourOverallClassPerformance/get?startDate=${startDate}&endDate=${endDate}&subjectId=${selectedSubject}`,
        );
        setStudentYourData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchstudentYourData();
  }, [startDate, endDate, student]);

  const isDataLoaded =
    studenttopperData && studentAverageData && studentYourData;

  const performanceData = isDataLoaded
    ? studenttopperData?.map((topperEntry) => {
        const matchingYourData = studentYourData?.find((yourEntry) => {
          return String(yourEntry?.id) === String(topperEntry.assessmentId);
        });

        const matchingAverageData = studentAverageData?.find(
          (avgEntry) =>
            String(avgEntry.assessmentId) === String(topperEntry.assessmentId),
        );

        return {
          assessmentName: topperEntry?.assessmentName,
          you: matchingYourData?.averagePercentageScore,
          topper: topperEntry?.topper?.percentage,
          average: matchingAverageData?.classAveragePercentage,
        };
      })
    : [];

  useEffect(() => {
    if (performanceData?.length > 0) {
      setLoading(false);
    }
  }, [performanceData]);

  const subjectsPerformance =
    subjectWiseData?.map((subject) => ({
      id: subject?.result?.[0]._id,
      name: subject?.result?.[0]?.name,
    })) || [];
  return (
    <div className="card-clsss-Analysis mt-2">
      <div className="card-content-clsss-Analysis">
        <div
          className="flex items-center justify-between mb-2 p-3 h-16 bg-gray-200"
          style={{ backgroundColor: "#edeef0" }}
        >
          <h2 className="card-title">Class Performance Analysis</h2>
          <div className="filter-group-class-analysis">
            <select
              value={selectedSupplement}
              onChange={(e) => setSelectedSupplement(e.target.value)}
              className="form-select"
            >
              <option value="">Supplements</option>
              <option value="assessment">Assessment</option>
            </select>

            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="form-select"
            >
              <option value="">Subject Name</option>
              {subjectList?.map((subject, index) => (
                <option key={subject.id} value={subject.id}>
                  {subject?.name}
                </option>
              ))}
            </select>

            <button
              className="clear-button"
              onClick={() => {
                setSelectedSupplement("");
                setSelectedSubject("");
              }}
              title="Clear Filter"
            >
              <FontAwesomeIcon icon={faFilterCircleXmark} />
            </button>
          </div>
        </div>

        <hr className="separator" />

        <div className="chart-wrapper">
          <div className="chart-container">
            {loading ? (
              <div className="no-data">No data available</div>
            ) : (
              <LineChart width={1200} height={400} data={performanceData}>
                <CartesianGrid vertical={false} />
                <YAxis />
                <Tooltip
                  content={({ payload }) => {
                    if (!payload || payload.length === 0) return null;
                    const assessmentName = payload[0]?.payload?.assessmentName;
                    return (
                      <div className="tooltip-box">
                        <div className="tooltip-title">{assessmentName}</div>
                        {payload.map((entry, index) => {
                          const value = Number(entry.value);
                          return (
                            <div key={index} style={{ color: entry.color }}>
                              {entry.name}:{" "}
                              <strong>{Math.round(value)}%</strong>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                />
                <Line
                  dataKey="you"
                  type="monotone"
                  stroke="#FF9800" // bright orange
                  strokeWidth={2}
                  dot={{
                    r: 6,
                    fill: "#FF5722",
                    stroke: "#BF360C",
                    strokeWidth: 2,
                  }}
                />

                <Line
                  dataKey="topper"
                  type="monotone"
                  stroke="#651FFF" // bright purple
                  strokeWidth={2}
                  dot={{
                    r: 6,
                    fill: "#7C4DFF",
                    stroke: "#311B92",
                    strokeWidth: 2,
                  }}
                />

                <Line
                  dataKey="average"
                  type="monotone"
                  stroke="#2196F3" // bright blue
                  strokeWidth={2}
                  dot={{
                    r: 6,
                    fill: "#03A9F4",
                    stroke: "#01579B",
                    strokeWidth: 2,
                  }}
                />
              </LineChart>
            )}
          </div>

          <div className="legend p-4">
            <div className="legend-item">
              <span
                className="dot"
                style={{ backgroundColor: "#FFCC80" }}
              ></span>
              <span>Your Score</span>
            </div>
            <div className="legend-item">
              <span
                className="dot"
                style={{ backgroundColor: "#7127f0" }}
              ></span>
              <span>Class Topper</span>
            </div>
            <div className="legend-item">
              <span
                className="dot"
                style={{ backgroundColor: "#90CAF9" }}
              ></span>
              <span>Class Average</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClassAnalysis;
